import { create } from "zustand";
import {
  TWebsocketSyncDataResponse,
  TOrganizationModelJSON
} from "@shoutout-labs/market_buzz_crm_types";
import { OnboardingProgress } from "@/services/OrganizationService";
import { OnboardingProgressService } from "@/services/OnboardingProgressService";

interface OrganizationStore {
  credits: number;
  setCredits: (credits: number) => void;
  dollarValue: number;
  setDollarValue: (dollarValue: number) => void;
  organization: TOrganizationModelJSON;
  setOrganization: (organization: TOrganizationModelJSON) => void;
  syncDataResponse: TWebsocketSyncDataResponse;
  setSyncDataResponse: (syncDataResponse: TWebsocketSyncDataResponse) => void;
  // Onboarding progress tracking
  completedStep1Actions: Set<string>;
  setCompletedStep1Actions: (actions: Set<string>) => void;
  completedStep2Actions: Set<string>;
  setCompletedStep2Actions: (actions: Set<string>) => void;
  // New onboarding progress state
  showOnboardingFlow: boolean;
  setShowOnboardingFlow: (show: boolean) => void;
  onboardingProgress: OnboardingProgress | null;
  setOnboardingProgress: (progress: OnboardingProgress | null) => void;
  // Organization ID for localStorage keys
  currentOrganizationId: string | null;
  setCurrentOrganizationId: (id: string | null) => void;
  // Refresh trigger for onboarding progress
  onboardingRefreshTrigger: number;
  refreshOnboardingProgress: () => void;
}

// Initialize from localStorage with organization-specific keys
const getInitialCompletedActions = (
  key: string,
  organizationId?: string
): Set<string> => {
  try {
    if (!organizationId) return new Set<string>();
    const orgSpecificKey = `${key}_${organizationId}`;
    const stored = localStorage.getItem(orgSpecificKey);
    return stored ? new Set(JSON.parse(stored)) : new Set<string>();
  } catch {
    return new Set<string>();
  }
};

export const useOrganizationStore = create<OrganizationStore>((set, get) => ({
  credits: 0,
  setCredits: (credits: number) => set({ credits }),
  dollarValue: 0,
  setDollarValue: (dollarValue: number) => set({ dollarValue }),
  organization: {} as TOrganizationModelJSON,
  setOrganization: (organization: TOrganizationModelJSON) =>
    set(() => ({
      organization
    })),
  syncDataResponse: { customer: 0, orders: 0 } as TWebsocketSyncDataResponse,
  setSyncDataResponse: (syncDataResponse: TWebsocketSyncDataResponse) =>
    set(() => ({
      syncDataResponse
    })),
  // Onboarding progress tracking
  completedStep1Actions: new Set<string>(),
  setCompletedStep1Actions: (actions: Set<string>) => {
    set({ completedStep1Actions: actions });
    // Persist to localStorage with organization-specific key
    const { currentOrganizationId } = get();
    if (currentOrganizationId) {
      localStorage.setItem(
        `completedStep1Actions_${currentOrganizationId}`,
        JSON.stringify(Array.from(actions))
      );
    }
  },
  completedStep2Actions: new Set<string>(),
  setCompletedStep2Actions: (actions: Set<string>) => {
    set({ completedStep2Actions: actions });
    // Persist to localStorage with organization-specific key
    const { currentOrganizationId } = get();
    if (currentOrganizationId) {
      localStorage.setItem(
        `completedStep2Actions_${currentOrganizationId}`,
        JSON.stringify(Array.from(actions))
      );
    }
  },
  // New onboarding progress state
  showOnboardingFlow: true,
  setShowOnboardingFlow: (show: boolean) => set({ showOnboardingFlow: show }),
  onboardingProgress: null,
  setOnboardingProgress: (progress: OnboardingProgress | null) =>
    set({ onboardingProgress: progress }),
  // Organization ID for localStorage keys
  currentOrganizationId: null,
  setCurrentOrganizationId: (id: string | null) => {
    set({ currentOrganizationId: id });

    // Clean up old localStorage entries to prevent cross-organization contamination
    OnboardingProgressService.clearOldOnboardingEntries();

    // Load organization-specific completed actions when organization changes
    if (id) {
      const step1Actions = getInitialCompletedActions(
        "completedStep1Actions",
        id
      );
      const step2Actions = getInitialCompletedActions(
        "completedStep2Actions",
        id
      );
      set({
        completedStep1Actions: step1Actions,
        completedStep2Actions: step2Actions
      });
    } else {
      // Clear actions when no organization is selected
      set({
        completedStep1Actions: new Set<string>(),
        completedStep2Actions: new Set<string>()
      });
    }
  },
  // Refresh trigger for onboarding progress
  onboardingRefreshTrigger: 0,
  refreshOnboardingProgress: () => {
    set((state) => ({
      onboardingRefreshTrigger: state.onboardingRefreshTrigger + 1
    }));
  }
}));
